/**
 * LiveAgentsDock — GM Attention Phase 1 (2026-07-17).
 *
 * App-level overlay (mirrors PiChatDock's mount): a floating button with a
 * pending-approval count badge that expands into a panel showing the live agent
 * roster (state chips) and pending approval cards with Approve / Deny. Resolving
 * calls POST /api/attention/respond; the agent's long-poll then unblocks.
 *
 * The "see agents as they run + approve while mobile" surface — same component
 * renders on desktop and the mobile-width shell; GM Mobile (iPhone) will later
 * consume the SAME endpoints natively.
 */
import { useCallback } from 'react';

import api from '../../services/api';
import { pendingCount, useAttentionStore } from '../../stores/attentionStore';
import { useAttentionStream } from '../../hooks/useAttentionStream';

const STATE_LABEL: Record<string, string> = {
  running: 'running',
  needs_approval: 'needs approval',
  needs_input: 'needs input',
  idle: 'idle',
  done: 'done',
  error: 'error',
};

export function LiveAgentsDock(): JSX.Element {
  useAttentionStream();
  const sessions = useAttentionStore((s) => s.sessions);
  const approvals = useAttentionStore((s) => s.approvals);
  const adapters = useAttentionStore((s) => s.adapters);
  const connected = useAttentionStore((s) => s.connected);
  const open = useAttentionStore((s) => s.open);
  const toggleOpen = useAttentionStore((s) => s.toggleOpen);
  const pending = pendingCount({ approvals });

  const respond = useCallback(
    async (approvalId: string, action: 'approve' | 'deny') => {
      try {
        await api.post('/attention/respond', { approval_id: approvalId, action });
      } catch (e) {
        console.error('[attention] respond failed:', (e as Error).message);
      }
    },
    [],
  );

  return (
    <div className="live-agents-dock" data-testid="live-agents-dock">
      <button
        className="live-agents-dock__fab"
        data-testid="live-agents-fab"
        onClick={() => toggleOpen()}
        title="Live Agents"
        aria-label={`Live Agents${pending ? ` — ${pending} pending approval` : ''}`}
      >
        <span className={`live-agents-dock__dot ${connected ? 'is-on' : 'is-off'}`} />
        Agents
        {pending > 0 && <span className="live-agents-dock__badge">{pending}</span>}
      </button>

      {open && (
        <div className="live-agents-dock__panel" data-testid="live-agents-panel">
          <div className="live-agents-dock__header">
            <span>Live Agents</span>
            <span className="live-agents-dock__conn">{connected ? '● live' : '○ offline'}</span>
          </div>

          {Object.keys(adapters).length > 0 && (
            /* Phase 2 — adapter health is API-visible by design (reviewer
             * rule): a dropped openclaw/opencode bridge shows here, not only
             * in backend logs. */
            <div className="live-agents-dock__adapters" data-testid="adapter-chips">
              {Object.entries(adapters).map(([name, state]) => (
                <span
                  key={name}
                  className={`live-agents-dock__adapter adapter--${state}`}
                  data-testid={`adapter-${name}`}
                  title={`${name} bridge: ${state}`}
                >
                  {name}: {state}
                </span>
              ))}
            </div>
          )}

          {approvals.length > 0 && (
            <div className="live-agents-dock__approvals">
              {approvals.map((a) => (
                <div key={a.approval_id} className="live-agents-dock__card" data-testid="approval-card">
                  <div className="live-agents-dock__card-title">{a.title}</div>
                  {a.command && <pre className="live-agents-dock__cmd">{a.command}</pre>}
                  {a.meta?.answerable === false ? (
                    /* Phase 2 — read-only mirror card (e.g. hermes' own local
                     * approval prompt): honest UI, no buttons that can't act. */
                    <div className="live-agents-dock__readonly" data-testid="readonly-note">
                      Answer in the agent&apos;s terminal — this card is
                      informational and clears when answered there.
                    </div>
                  ) : (
                    <div className="live-agents-dock__actions">
                      <button
                        className="live-agents-dock__approve"
                        data-testid="approve-btn"
                        onClick={() => void respond(a.approval_id, 'approve')}
                      >
                        Approve
                      </button>
                      <button
                        className="live-agents-dock__deny"
                        data-testid="deny-btn"
                        onClick={() => void respond(a.approval_id, 'deny')}
                      >
                        Deny
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="live-agents-dock__roster">
            {sessions.length === 0 && (
              <div className="live-agents-dock__empty">No active agents.</div>
            )}
            {sessions.map((s) => (
              <div key={s.session_id} className="live-agents-dock__row" data-testid="agent-row">
                <span className={`live-agents-dock__state state--${s.state}`}>
                  {STATE_LABEL[s.state] ?? s.state}
                </span>
                <span className="live-agents-dock__name">{s.display_name}</span>
                {s.last_activity && (
                  <span className="live-agents-dock__activity">{s.last_activity}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
